<template>
  <div
    v-if="warning"
    role="status"
    aria-live="polite"
    :title="warning.details"
    :aria-label="warning.details"
    class="mt-1 text-xs leading-tight text-amber-700"
    :class="showComparison ? 'whitespace-normal' : 'whitespace-nowrap'"
  >
    <i class="fas fa-exclamation-triangle mr-1" aria-hidden="true"></i>
    {{ warning.kind === 'discrepancy' ? 'Écart de poids trop important' : 'Poids à compléter' }}
    <span v-if="showComparison && warning.kind === 'discrepancy'">
      ({{ warning.comparison }})
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  getCompleteParcelWeight,
  getParcelWeightDiscrepancy,
  type DeclaredParcelWeight,
} from "@/utils/parcelWeightDiscrepancy";

const props = defineProps<{
  calculatedWeight?: number | null;
  confirmedParcels?: DeclaredParcelWeight[];
  confirmedCount?: number;
  parcels?: DeclaredParcelWeight[] | null;
  expectedCount?: number;
  showComparison?: boolean;
}>();

const formatWeight = (weight: number, maximumFractionDigits = 3) =>
  weight.toLocaleString("fr-FR", { maximumFractionDigits });

const warning = computed(() => {
  // Aucun poids confirmé : estimation ; confirmation partielle : pas de comparaison.
  const confirmedWeight = props.confirmedParcels?.some((parcel) => Number(parcel?.poids) > 0)
    ? getCompleteParcelWeight(props.confirmedParcels, props.confirmedCount)
    : undefined;
  const value = getParcelWeightDiscrepancy(
    confirmedWeight !== undefined ? confirmedWeight : props.calculatedWeight,
    Array.isArray(props.parcels) ? props.parcels.slice(0, props.expectedCount) : [],
    props.expectedCount,
  );
  if (!value) return null;
  if (value.kind === "incomplete") {
    return { ...value, details: "Renseignez le poids de tous les colis pour vérifier l’écart de poids." };
  }
  const label = confirmedWeight !== undefined ? "Poids confirmé" : "Poids calculé";
  const comparison = `${label} : ${formatWeight(value.reference)} kg · Poids déclaré : ${formatWeight(value.declared)} kg`;
  return {
    ...value,
    comparison,
    details: `${comparison} · Écart : ${formatWeight(value.percentage, 1)} %`,
  };
});
</script>
